import type { SecurePerson } from "features/secureUploads/secureUploads";
import { formatApiError } from "utils/formatApiError";

const theFiles = (files: number) => (files > 1 ? "these files" : "this file");

/** The confirm button: who joins the project on the way, or how many files go. */
export function moveConfirmLabel(losing: SecurePerson[], files = 1): string {
  if (losing.length === 1)
    return `Add ${losing[0].name} to the project and move`;
  if (losing.length > 1)
    return `Add ${losing.length} people to the project and move`;
  return files > 1 ? `Move ${files} files` : "Move";
}

/** The warning over the people a move would leave out. Whoever cannot add people
 * to a project (a client user) is told who can. */
export function losingLine(
  losing: SecurePerson[],
  projectName: string,
  files = 1,
  canAdd = true,
): string {
  const line =
    losing.length === 1
      ? `${losing[0].name} isn't on ${projectName} and would lose access to ${theFiles(files)}.`
      : `These people aren't on ${projectName} and would lose access to ${theFiles(files)}.`;
  return canAdd
    ? line
    : `${line} Ask your Calibrate team to add them to the project first.`;
}

/** Where a file is now: "Individual Files", the project, or "project / collection". */
export function placeLabel(file: {
  project_name: string | null;
  collection_name: string | null;
}): string {
  if (!file.project_name) return "Individual Files";
  return file.collection_name
    ? `${file.project_name} / ${file.collection_name}`
    : file.project_name;
}

/** Where the files of a move come from: one place, or "Several places". */
export function sourceLabel(
  files: { project_name: string | null; collection_name: string | null }[],
): string {
  const places = new Set(files.map(placeLabel));
  return places.size === 1 ? Array.from(places)[0] : "Several places";
}

/** The collection choices a move inside a project leaves out: every place one of
 * the files already is - its root ("root") or a collection id. */
export function placesTaken(
  files: { collection_id: number | null }[],
): Set<string> {
  return new Set(
    files.map((file) =>
      file.collection_id == null ? "root" : String(file.collection_id),
    ),
  );
}

/** The note over the people a move would show the files to. */
export function gainingLine(gaining: SecurePerson[], files = 1): string {
  const what = files > 1 ? "the files" : "the file";
  return gaining.length === 1
    ? `This person will newly see ${what} there:`
    : `These people will newly see ${what} there:`;
}

/** What a refused move says. A refusal that names who would lose the files
 * carries them as a list beside its sentence - only the sentence is shown. */
export function moveRefusal(data: unknown, fallback: string): string {
  const error = (data as { error?: unknown } | null)?.error;
  if (typeof error === "string" && error) return error;
  return formatApiError(data, fallback);
}
